import { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { DefaultSearchTopicHint } from "components/notices_v2/DefaultSearchTopicHint";
import { EditDefaultSearchTopicModal } from "components/notices_v2/EditDefaultSearchTopicModal";
import { TopicSearchHelperPopover } from "components/notices_v2/TopicSearchHelperPopover";
import Button from "components/ui/button/Button";
import TextAreaInput from "components/ui/input/TextAreaInput";
import { EventNames, useTracking } from "lib/tracking";

export const NOTICE_V2_TOPIC_SEARCH_PLACEHOLDER = "Search by topic, service or notice description";

type TopicSearchSectionProps = {
  topicInput: string;
  onSearch: (value: string) => void;
  disabled: boolean;
  onCollapseSidebar?: () => void;
  showCollapseButton?: boolean;
  defaultSearchTopic?: string;
  showDefaultSearchTopicHint?: boolean;
  onDefaultSearchTopicUpdated?: (topic: string) => void;
};

export function TopicSearchSection({
  topicInput,
  onSearch,
  disabled,
  onCollapseSidebar,
  showCollapseButton = false,
  defaultSearchTopic = "",
  showDefaultSearchTopicHint = false,
  onDefaultSearchTopicUpdated,
}: TopicSearchSectionProps) {
  const [topic, setTopic] = useState(topicInput);
  const [isEditDefaultTopicModalOpen, setIsEditDefaultTopicModalOpen] = useState(false);
  const { logEvent } = useTracking();

  // Sync local state when the parent updates value (e.g. clear filters). useState(value) only
  // uses value on initial mount; state is preserved across re-renders and does not update when
  // the prop changes.
  useEffect(() => {
    setTopic(topicInput);
  }, [topicInput]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSearch(topic);
        logEvent(EventNames.topicSearchPerformed, {
          Topic: topic,
          "Context source": "Notices 2.0",
        });
      }
    },
    [topic, onSearch, logEvent],
  );

  return (
    <Flex column>
      <Header>
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <Flex alignItems="center" gap={4}>
            <HeaderText h3>Search topic</HeaderText>
            <TopicSearchHelperPopover />
          </Flex>
          {onCollapseSidebar && showCollapseButton && (
            <Button
              buttonType="secondary"
              icon="chevronLeftDouble"
              onClick={onCollapseSidebar}
              aria-label="Close filters"
            />
          )}
        </Flex>
      </Header>
      <Body column gap={16} aria-label="Topic search">
        {showDefaultSearchTopicHint && (
          <DefaultSearchTopicHint onEditClick={() => setIsEditDefaultTopicModalOpen(true)} />
        )}
        <TextAreaInput
          name="topicSearch"
          value={topic}
          onChange={(e) => setTopic(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          placeholder={NOTICE_V2_TOPIC_SEARCH_PLACEHOLDER}
          minRows={4}
          autoComplete="off"
          aria-label="Topic search"
          disabled={disabled}
        />
        <Button
          buttonType="secondary"
          icon="searchSm"
          onClick={() => {
            onSearch(topic);
            logEvent(EventNames.topicSearchPerformed, {
              Topic: topic,
              "Context source": "Notices 2.0",
            });
          }}
          disabled={disabled}
        >
          Search
        </Button>
      </Body>
      {showDefaultSearchTopicHint && (
        <EditDefaultSearchTopicModal
          open={isEditDefaultTopicModalOpen}
          onOpenChange={setIsEditDefaultTopicModalOpen}
          defaultSearchTopic={defaultSearchTopic}
          onSuccess={onDefaultSearchTopicUpdated}
        />
      )}
    </Flex>
  );
}

const Header = styled(Flex)(({ theme }) => ({
  padding: "24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  color: theme.colors.sysTextDefault,
}));

const HeaderText = styled(Text)({
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "125%",
});

const Body = styled(Flex)(({ theme }) => ({
  padding: "24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));
